import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../hooks/hooks";
import { useGetAcademicYearQuery } from "../../../hooks/useGetAcademicYearQuery";
import { useGetCoursesQuery } from "../../../hooks/useGetCoursesQuery";
import { useGetDepartamentsQuery } from "../../../hooks/useGetDepartamentsQuery";
import { useGetFacultiesQuery } from "../../../hooks/useGetFacultiesQuery";
import { useGetStudentsSubscriptionSubjectsQuery } from "../../../hooks/useGetStudentsSubscriptionSubjectsQuery";
import { useGetUniversitiesQuery } from "../../../hooks/useGetUniversitiesQuery";
import { getPlanSubjects } from "../../../services/school/PlanSubjects";
import { PAGE_SIZE, PAGE_NUMBER } from "../../../utils/constants";
import { Card, Flex, Heading } from "@radix-ui/themes";
import RadioGroupComponent from "../../../components/inputs/RadioGroup/RadioGroupComponent";
import SelectComponent from "../../../components/inputs/Select/SelectComponent";
import TextFieldComponent from "../../../components/inputs/Textfield/TextfieldComponent";
import { useGetMonthPayments } from "../../../hooks/useGetStudentDebtiesQuery";
import { Lists } from "../../../components/common/Lists";
import TableComponent from "../../../components/common/Tables";
import { useGetPaymentTypesQuery } from "../../../hooks/useGetPaymentTypesQuery";
const columnNamesMap: { [key: string]: string } = {
    code: 'Nome',
    fullName: 'Estudante',
    january: 'Janeiro',
    february: 'Fevereiro',
    march: 'Março',
    april: 'Abril',
    may: 'Maio',
    june: 'Junho',
    july: 'Julho',
    august: 'Agosto',
    september: 'Setembro',
    october: 'Outubro',
    november: 'Novembro',
    december: 'Dezembro',
    totalPaid: 'Total Pago',
    paymentTypes: 'Tipo de Pagamento'
};

export const MonthPayments = () => {

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const { data: academicYears, isLoading: isLoadingAcademicYears, isError: errorAcademicYears } = useGetAcademicYearQuery({ PageNumber: 1, PageSize: 400 });

    type Filters = {
        IdFaculty?: string;
        IdUniversity?: string;
        IdDepartament?: string;
        IdCourse?: string;
        IdSubject?: string;
        IdAcademicYear?: string;
        Regime: number;
        Level: number;
        Semester: number;
        SemesterSubject?: number;
        LevelSubject?: number;
        idPaymentType?: string;
    }
    const { control, handleSubmit, formState: { errors }, getValues, setValue, trigger, watch, reset, resetField } = useForm<Filters>({
        defaultValues: {},
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        shouldUseNativeValidation: false,
    });
    const filters = watch();
    const { data, status } = useGetStudentsSubscriptionSubjectsQuery({ PageNumber: currentPage, PageSize: pageSize, SearchTerm: searchTerm, ...filters });

    const { data: universities, isLoading: isLoadingUniversities, isError: errorUniversities, refetch: refetchUniversities } = useGetUniversitiesQuery({ PageNumber: 1, PageSize: 400 });

    const { data: faculties, isLoading: isLoadingFaculties, isError: errorFaculties, refetch: refetchFaculties } = useGetFacultiesQuery({ PageNumber: 1, PageSize: 400, IdUniversity: getValues('IdUniversity') });

    const { data: departaments, isLoading: isLoadingDepartaments, isError: errorDepartaments, refetch: refreshDepartaments } = useGetDepartamentsQuery(
        { PageNumber: 1, PageSize: 400, IdFaculty: getValues('IdFaculty') },
        { enabled: !!getValues('IdFaculty') }
    );
    const { data: courses, isLoading: isLoadingCourses, isError: errorCourses, refetch: refreshCourses } = useGetCoursesQuery({ PageNumber: 1, PageSize: 400, IdDepartament: getValues('IdDepartament') },
        { enabled: !!getValues('IdDepartament') }
    );


    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };
    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(PAGE_NUMBER);
    };

    const handleSearch = (term: string) => {
        setSearchTerm(term);
        setCurrentPage(1);
    };
    useEffect(() => {
        if (academicYears?.items && academicYears.items.length > 0) {
            if (!getValues('IdAcademicYear')) {
                const mostRecentYear = academicYears.items[academicYears.items.length - 1];
                setValue('IdAcademicYear', mostRecentYear.id);
            }
        }
    }, [academicYears, setValue, getValues]);


    const onSubmit = handleSubmit(async (data) => {
    });
    const handleLevelChange = async (e: any) => {
        var level = e.target.value;
        setValue("Level", level);
    }
    const handleSemesterChange = async (e: any) => {
        var semester = e.target.value;
        setValue("Semester", semester);
    }
    const handleLevelSubjectChange = async (e: any) => {
        var level = e.target.value;
        setValue("LevelSubject", level);
        refreshPlanSubjects();
    }
    const handleSemesterSubjectChange = async (e: any) => {
        var semester = e.target.value;
        setValue("SemesterSubject", semester);
        refreshPlanSubjects();
    }
    const handleUniversityChange = (id: string) => {
        setValue('IdUniversity', id);
        setValue('IdFaculty', '');
        setValue('IdDepartament', '');
        setValue('IdCourse', '');
        refetchFaculties();
    };

    const handleFacultyChange = (idFaculty: string) => {
        setValue('IdFaculty', idFaculty);
        refreshDepartaments();
    };

    const handleDepartamentChange = (idDepartament: string) => {
        setValue('IdDepartament', idDepartament);
        refreshCourses();
    };

    const handleCourseChange = async (idCourse: string) => {
        setValue('IdCourse', idCourse);
        refreshPlanSubjects()
    }

    const { data: planSubjectsData, isLoading: isLoadingPlanSubjectsData, isError: isErrorPlanSubjectsData, refetch: refreshPlanSubjects, status: planSubjectsStatus } =
        useQuery(
            ['getPlanSubjectsQuery'],
            async () => {
                try {
                    const data = await getPlanSubjects({
                        PageSize: 400,
                        PageNumber: 1,
                        Level: getValues('LevelSubject'),
                        Semester: getValues('SemesterSubject'),
                        IdAcademicYear: getValues('IdAcademicYear'),
                        IdCourse: getValues('IdCourse')
                    }
                    );
                    const responseData = { ...data }
                    return responseData;
                } catch { }

            },
            {
                enabled: !!getValues('IdCourse'),
                refetchOnWindowFocus: false,
                retry: false
            }
        );

    const { data: monthPaymentsData, status: monthPaymentsStatus } = useGetMonthPayments(
        { idStudents: data?.items.map(i => i.id ?? '') || [],
             idPaymentType: watch('idPaymentType')??"" }, { enabled: !!data?.items });
    const { data: paymentTypesData } = useGetPaymentTypesQuery({ PageNumber: 1, PageSize: 4000 });


    return (
        <>
            <Card>
                <Flex gap="4" direction="column">
                    <Heading>Controle de pagamentos dos estudantes - Ano {(academicYears?.items.find(i => i.id === watch('IdAcademicYear'))?.year) ?? 'Todos'}</Heading>
                    <form onSubmit={onSubmit}>
                        <Flex gap="4" direction="column">
                            <Flex gap='2'>
                                <SelectComponent name="IdAcademicYear" label='Ano Acadêmico' control={control} errors={errors} style={{ width: '35%' }}
                                    options={academicYears?.items.map(ac => ({
                                        value: ac.id ?? "",
                                        label: ac.year ?? ""
                                    }))}
                                    itemsError={errorAcademicYears}
                                    itemsLoading={isLoadingAcademicYears}
                                />
                                <SelectComponent name="idPaymentType" label="Tipo de pagamento" control={control} errors={errors} style={{ width: '100%' }}
                                    options={paymentTypesData?.items.map((paymentType) => ({
                                        value: paymentType?.id ?? "",
                                        label: paymentType?.description ?? ""
                                    }))}
                                    required={true}
                                />
                                <RadioGroupComponent name="Regime" label="Regime" control={control} errors={errors} style={{ width: '100%' }}
                                    options={[
                                        { value: "1", label: "Laboral" }
                                        , { value: "2", label: "Pós Laboral" }
                                        , { value: "3", label: "Distância" }
                                    ]}
                                    fieldProps={{
                                        style: { display: 'flex', flexDirection: 'row', gap: '16px' }
                                    }}
                                />
                            </Flex>
                            <Flex gap='4'>
                                <TextFieldComponent
                                    name="Level"
                                    label="Ano"
                                    control={control}
                                    errors={errors}
                                    fieldProps={{ placeholder: 'Ano', onChange: handleLevelChange, type: 'number' }} style={{ width: '100%' }}
                                />
                                <TextFieldComponent
                                    name="Semester"
                                    label="Semestre"
                                    control={control}
                                    errors={errors}
                                    fieldProps={{ placeholder: 'Semestre', onChange: handleSemesterChange, type: 'number' }} style={{ width: '100%' }}

                                />
                            </Flex>
                            <Flex gap='4'>
                                <SelectComponent
                                    name="IdUniversity"
                                    label="Instituição de Ensino Superior"
                                    control={control}
                                    errors={errors}
                                    style={{ width: '100%' }}
                                    options={universities?.items.map(u => ({
                                        value: u.id ?? "",
                                        label: u.description ?? ""
                                    }))}
                                    itemsLoading={isLoadingUniversities}
                                    itemsError={errorUniversities}
                                    onSelect={handleUniversityChange}
                                    required={true}
                                />
                                <SelectComponent name="IdFaculty" label='Unidade orgânica' control={control} errors={errors} style={{ width: '100%' }}
                                    options={faculties?.items.map(faculty => ({
                                        value: faculty.id ?? "",
                                        label: faculty.description ?? ""
                                    }))}
                                    onSelect={handleFacultyChange}
                                    itemsError={errorFaculties}
                                    itemsLoading={isLoadingFaculties}
                                />
                                <SelectComponent name="IdDepartament" label='Departamento' control={control} errors={errors} style={{ width: '100%' }}
                                    options={departaments?.items.map(departament => ({
                                        value: departament.id ?? "",
                                        label: departament.description ?? ""
                                    }))}
                                    onSelect={handleDepartamentChange}
                                    itemsError={errorDepartaments}
                                    itemsLoading={isLoadingDepartaments}
                                />

                            </Flex>
                            <Flex gap='4' direction='column'>


                                <SelectComponent name="IdCourse" label='Curso' control={control} errors={errors} style={{ width: '100%' }}
                                    options={courses?.items.map(course => ({
                                        value: course.id ?? "",
                                        label: course.name ?? ""
                                    }))}
                                    onSelect={
                                        handleCourseChange
                                    }
                                    itemsError={errorCourses}
                                    itemsLoading={isLoadingCourses}

                                />

                                <Flex gap='4'>
                                    <TextFieldComponent
                                        name="LevelSubject"
                                        label="Ano da disciplina"
                                        control={control}
                                        errors={errors}
                                        fieldProps={{ placeholder: 'Ano', onChange: handleLevelSubjectChange, type: 'number' }} style={{ width: '100%' }}
                                    />
                                    <TextFieldComponent
                                        name="SemesterSubject"
                                        label="Semestre da disciplina"
                                        control={control}
                                        errors={errors}
                                        fieldProps={{ placeholder: 'Semestre', onChange: handleSemesterSubjectChange, type: 'number' }} style={{ width: '100%' }}

                                    />
                                </Flex>
                                <SelectComponent
                                    name="IdSubject"
                                    label="Disciplina"
                                    control={control}
                                    errors={errors}
                                    options={planSubjectsData?.items.map(subject => ({
                                        value: subject.idSubject ?? "",
                                        label: subject.subject ?? ""
                                    }))}
                                    style={{ width: '100%' }}
                                    itemsError={isErrorPlanSubjectsData}
                                    itemsLoading={isLoadingPlanSubjectsData}
                                />
                            </Flex>
                        </Flex>

                    </form>
                    <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
                    </Flex>
                </Flex>
            </Card>


            <Lists
                status={monthPaymentsStatus}
                TableComponent={() =>
                    <TableComponent
                        columns={Object.keys(columnNamesMap)}
                        items={monthPaymentsData || []}
                        options={[]}
                        columnNamesMap={columnNamesMap}
                        totalPages={1}
                        currentPage={1}
                        onPageChange={() => { }}
                        // pageSize={pageSize}
                        // onPageSizeChange={() => { }}
                        // onSearch={() => { }}
                        enableExportToExcel={true}
                        totalItems={monthPaymentsData?.length || 0}
                    />
                }
                handleClickCreate={undefined}
            />

        </>
    )
}
